/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react'

import { COLUMN_NAMES, DueOutFetchArgs } from '../../assets/ts/data/dueout/dueout'
import Buttons from '../../components/buttons/buttons'
import { MetadataContext } from '../../store/metadata-context-provider'
import {
  ALL_SELECT_OPTION,
  ALL_SELECT_OPTION_STRING,
  NONE_SELECT_OPTION,
  NONE_SELECT_OPTION_STRING,
} from '../../utils/constants/global-constants'
import CustomSelect from '../../utils/controls/custom-select'

type Props = {
  availableFilterValues: Map<string, number[] | string[]>
  filter: DueOutFetchArgs
  onClearAll: () => void
  onUpdateFilter: (args: DueOutFetchArgs, order: number) => void
  tabSelected :number
}
interface SelectOption {
  label: string
  value: null | number | string
}

interface FilterDropdown {
  AssignedComponentCode?: SelectOption[]
  FY?: SelectOption[]
  SectionCode?: SelectOption[]
  ServicingBy?: SelectOption[]
  ServicingMAJCOMCode?: SelectOption[]
  ServicingOrganization?: SelectOption[]
}
let filterChanged = 0
const CustomerDashboardDueOutFilters = ({
  availableFilterValues,
  filter,
  onClearAll,
  onUpdateFilter,
  tabSelected,
}: Props) => {
  const metadataContext = useContext(MetadataContext)

  const [dropdownOptions, setDropdownOptions] = useState<FilterDropdown>({})
  const filterOrder = {
    AssignedComponentCode: 3,
    FY: 1,
    SectionCode: 7,
    ServicingBy: 5,
    ServicingMAJCOMCode: 4,
    ServicingOrganization: 6,
  }

  const filterValues = (name) => {
    return availableFilterValues?.get(name) ?? []
  }

  const filterChangedOrder = (e, selectorder: number) => {
    filterChanged = selectorder
  }
  const generateCodeOptionOnLength = (
    metadataOptions: SelectOption[],
    codes: (null | number)[]
  ) => {
    const options = metadataOptions.filter((option) => codes.includes(option.value as number))
    if (codes.includes(null)) {
      options.unshift({ label: '(Empty)', value: null })
    }
    return options.length === 0
      ? [NONE_SELECT_OPTION]
      : options.length === 1
      ? options
      : [ALL_SELECT_OPTION, ...options]
  }

  const createCodeOptions = (
    filterChanged: number,
    threshold: number,
    dropdownOptions: SelectOption[],
    metadataOptions: SelectOption[],
    codes: number[]
  ) => {
    return filterChanged >= threshold
      ? dropdownOptions
      : generateCodeOptionOnLength(metadataOptions, codes)
  }
  const generateStringOptionOnLength = (values: string[]) => {
    const options = values.map((value) => ({
      label: value,
      value: value,
    }))
    if (options.length === 0) {
      return [NONE_SELECT_OPTION_STRING]
    }else  if (options.length === 1) {
      return options
    } else if (options.length > 1) {
      return [ALL_SELECT_OPTION_STRING, ...options]
    }
  }

  const createStringOptions = (
    filterChanged: number,
    threshold: number,
    dropdownOptions: SelectOption[],
    values: string[]
  ) => {
    if (filterChanged >= threshold) {
      return dropdownOptions
    }
    return generateStringOptionOnLength(values)
  }
  useEffect(()=>{
    filterChanged = 0
  },[tabSelected])

  useEffect(() => {
    if (filterChanged >= 0) {
      const yearFilter: SelectOption[] = filterValues(COLUMN_NAMES.FY).map((v) => ({
        label: v,
        value: v,
      }))
      const yearOptions =
        filterChanged === 0 && yearFilter.length === 0
          ? [NONE_SELECT_OPTION]
          : yearFilter.length === 1
          ? yearFilter
          : filterChanged === 0
          ? [ALL_SELECT_OPTION, ...yearFilter]
          : dropdownOptions.FY

          const componentCodes = filterValues(COLUMN_NAMES.AssignedComponentCode) as number[]
          const componentOptions = createCodeOptions(
            filterChanged,
            filterOrder.AssignedComponentCode,
            dropdownOptions.AssignedComponentCode as SelectOption[],
            metadataContext.componentOptions,
            componentCodes
          )
      const majcomCodes = filterValues(COLUMN_NAMES.ServicingMAJCOMCode) as number[]
      const majcomOptions = createCodeOptions(
        filterChanged,
        filterOrder.ServicingMAJCOMCode,
        dropdownOptions.ServicingMAJCOMCode as SelectOption[],
        metadataContext.majcomOptions,
        majcomCodes
      )
      const servicingByValues = filterValues(COLUMN_NAMES.ServicingBy) as string[]
      const servicingByOptions = createStringOptions(
        filterChanged,
        filterOrder.ServicingBy,
        dropdownOptions.ServicingBy as SelectOption[],
        servicingByValues
      )

      const servicingOrganizationValues = filterValues(
        COLUMN_NAMES.ServicingOrganization
      ) as string[]
      const servicingOrganizationOptions = createStringOptions(
        filterChanged,
        filterOrder.ServicingOrganization,
        dropdownOptions.ServicingOrganization as SelectOption[],
        servicingOrganizationValues
      )
      const sectionCodes = filterValues(COLUMN_NAMES.SectionCode) as number[]
      const sectionOptions = createCodeOptions(
        filterChanged,
        filterOrder.SectionCode,
        dropdownOptions.SectionCode as SelectOption[],
        metadataContext.sectionOptions,
        sectionCodes
      )

      setDropdownOptions((prev) => ({
        ...prev,
        AssignedComponentCode: componentOptions as SelectOption[],
        FY: yearOptions as SelectOption[],
        SectionCode: sectionOptions as SelectOption[],
        ServicingBy: servicingByOptions as SelectOption[],
        ServicingMAJCOMCode: majcomOptions as SelectOption[],
        ServicingOrganization: servicingOrganizationOptions as SelectOption[],
      }))
    }
  }, [availableFilterValues, filterChanged])

  return (
    <div className='pb-2 ps-sm-3 d-flex flex-column flex-sm-row align-items-sm-center w-100 maxfitcontent-sm ms-auto dashboardfilters'>
      <div className='d-flex flex-wrap flex-column flex-sm-row justify-content-start gap-2'>
        <CustomSelect
          className='form-sm w-100 max-w-80 min-w-80 sourcesanspro font-13 customselect'
          formClassName='form-horizontal dash poppins-semibold font-12 title-color'
          id='Year'
          label='Year'
          name='Year'
          onChange={(e) => {
            onUpdateFilter(
              {
                yearFilter: e.value,
              },
              filterOrder.FY
            )
            filterChangedOrder(e, filterOrder.FY)
          }}
          options={dropdownOptions.FY}
          value={
            dropdownOptions.FY?.length === 1
              ? dropdownOptions.FY[0].value
              : filter.yearFilter
          }
        />
        <CustomSelect
          className='form-sm w-100 max-w-80 min-w-80 sourcesanspro font-13 customselect'
          formClassName='form-horizontal dash poppins-semibold font-11 title-color'
          id='AssignComp'
          label='Assigned Component'
          name='Assigned Componrnt'
          onChange={(e) => {
            onUpdateFilter(
              {
                assignedComponentCodeFilter: e.value,
              },
              filterOrder.AssignedComponentCode
            )
            filterChangedOrder(e, filterOrder.AssignedComponentCode)
          }}
          options={dropdownOptions.AssignedComponentCode}
          value={
            dropdownOptions.AssignedComponentCode?.length === 1
              ? dropdownOptions.AssignedComponentCode[0].value
              : filter.assignedComponentCodeFilter
          }
        />
        <CustomSelect
          className='form-sm w-100 max-w-80 min-w-80 sourcesanspro font-13 customselect'
          formClassName='form-horizontal dash poppins-semibold font-11 title-color'
          id='Servicing MAJCOM'
          label='Servicing MAJCOM'
          name='ServicingMAJCOM'
          onChange={(e) => {
            onUpdateFilter({ servicingMAJCOMCodeFilter: e.value }, filterOrder.ServicingMAJCOMCode)
            filterChangedOrder(e, filterOrder.ServicingMAJCOMCode)
          }}
          options={dropdownOptions.ServicingMAJCOMCode}
          value={
            dropdownOptions.ServicingMAJCOMCode?.length === 1
              ? dropdownOptions.ServicingMAJCOMCode[0].value
              : filter.servicingMAJCOMCodeFilter
          }
        />

        <CustomSelect
          className='form-sm w-100 max-w-80 min-w-80 sourcesanspro font-13 customselect'
          formClassName='form-horizontal dash poppins-semibold font-12 title-color'
          id='CurrentServicingUnit'
          label='Current Servicing Unit'
          name='Current Servicing Unit'
          onChange={(e) => {
            onUpdateFilter(
              {
                servicingByFilter: e.value,
              },
              filterOrder.ServicingBy
            )
            filterChangedOrder(e, filterOrder.ServicingBy)
          }}
          options={dropdownOptions.ServicingBy}
          value={
            dropdownOptions.ServicingBy?.length === 1
              ? dropdownOptions.ServicingBy[0].value
              : filter.servicingByFilter
          }
        />
          <CustomSelect
          className='form-sm w-100 max-w-80 min-w-80  sourcesanspro font-13 customselect'
          formClassName='form-horizontal dash poppins-semibold font-11'
          id='ServicingOrganization'
          label='Servicing Organization'
          name='Servicing Organization'
          onChange={(e) => {
            onUpdateFilter(
              { servicingOrganizationFilter: e.value },
              filterOrder.ServicingOrganization
            )
            filterChangedOrder(e, filterOrder.ServicingOrganization)
          }}
          options={dropdownOptions.ServicingOrganization}
          value={
            dropdownOptions.ServicingOrganization?.length === 1
              ? dropdownOptions.ServicingOrganization[0].value
              : filter.servicingOrganizationFilter
          }
        />
        <CustomSelect
          className='form-sm w-100 max-w-80 min-w-80 sourcesanspro font-13 customselect'
          formClassName='form-horizontal dash poppins-semibold font-12 title-color'
          id='DasboardSection'
          label='Section'
          name='Section'
          onChange={(e) => {
            onUpdateFilter(
              {
                sectionCodeFilter: e.value,
              },
              filterOrder.SectionCode
            )
            filterChangedOrder(e, filterOrder.SectionCode)
          }}
          options={dropdownOptions.SectionCode}
          value={
            dropdownOptions.SectionCode?.length === 1
              ? dropdownOptions.SectionCode[0].value
              : filter.sectionCodeFilter
          }
        />
      </div>
      <Buttons
        aria-label='Clear All'
        className='btn btn-sm links ms-auto ms-sm-0 ms-md-0  links-underline text-nowrap'
        label='Clear All'
        onClick={() => {
          onClearAll()
        }}
      />
    </div>
  )
}
export default CustomerDashboardDueOutFilters
